import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import CustomButton from "../CustomButton";
import DataTable, { Column } from "../DataTable";
import ModalLayout from "../layouts/ModalLayout";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchStaff } from "../../features/staff/staffSlice";
import { AddStaffForm } from "../../types/staff";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import classesApis from "../../api/classesApis";
import { useBranchCode } from "../../util/hooks/useBranch";
import { AssignedTeacher } from "../../types/classesTypes";
import { formatRoleForDisplay } from "../../util/functions/UniversalFunctions";

interface Props {
  onClose?: () => void;
  onSuccess?: () => void;
}

export default function AssignTeachersToClass({ onClose, onSuccess }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const { staffList, filters } = useSelector((state: RootState) => state.staff);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<number[]>([]);

  const { classId } = useParams<{ classId: string }>();
  const parsedClassId = Number(classId);
  const branchCode = useBranchCode();

  useEffect(() => {
    const fetchCurrentlyAssigned = async () => {
      try {
        const res = await classesApis.fetchAssignedTeachers(parsedClassId);
        if (res?.data) {
          // Extract just the IDs from the assigned teachers list
          const assignedIds = res.data.map((t: AssignedTeacher) => t.staffId);
          setSelectedTeacherIds(assignedIds);
        }
      } catch (error) {
        console.error("Failed to fetch assigned teachers", error);
      }
    };

    if (parsedClassId) fetchCurrentlyAssigned();
  }, [parsedClassId]);

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchStaff({
        ...filters,
        branchCode,
        page: 0,
        size: 100,
      }),
    );
  }, [dispatch, filters, branchCode]);

  const handleAssignTeacher = async () => {
    if (!selectedTeacherIds.length) {
      toast.error("Please select at least one teacher");
      return;
    }

    try {
      await Promise.all(
        selectedTeacherIds.map((teacherId) =>
          classesApis.assignTeacherToClass(parsedClassId, teacherId),
        ),
      );

      toast.success("Teachers assigned successfully");
      onSuccess?.();
      onClose?.();
    } catch (error) {
      toast.error("Failed to assign teachers");
      console.error(error);
    }
  };

  const toggleTeacherSelection = (staffId: number) => {
    setSelectedTeacherIds((prev) =>
      prev.includes(staffId)
        ? prev.filter((id) => id !== staffId)
        : [...prev, staffId],
    );
  };

  const handleSelectAll = () => {
    if (selectedTeacherIds.length === staffList.length) {
      setSelectedTeacherIds([]);
    } else {
      setSelectedTeacherIds(staffList.map((staff) => staff.staffId));
    }
  };

  const columns: Column<AddStaffForm>[] = [
    {
      label: (
        <input
          type="checkbox"
          className="w-3 h-3"
          checked={
            staffList.length > 0 &&
            selectedTeacherIds.length === staffList.length
          }
          onChange={handleSelectAll}
        />
      ),
      accessor: "select",
      render: (item) => (
        <input
          type="checkbox"
          className="w-3 h-3"
          checked={selectedTeacherIds.includes(item.staffId)}
          onChange={() => toggleTeacherSelection(item.staffId)}
        />
      ),
    },

    { label: "SL NO.", accessor: "staffId" },
    {
      label: "Profile",
      accessor: "fullName",
      render: (item) => (
        <div>
          {!item?.profile_image_url ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.profile_image_url}
              alt="profile"
              className="w-10 h-10 rounded-full object-cover bg-background-200"
            />
          )}
        </div>
      ),
    },
    {
      label: "Staff Name",
      accessor: "staffName",
      render: (item) => (
        <div className="font-bold text-sm text-neutral-400">
          {item.fullName}
        </div>
      ),
    },
    {
      label: "Designation",
      accessor: "designation",
      render: (item) => (
        <div className="text-xs text-neutral-300">
          {formatRoleForDisplay(item.roleName)}
        </div>
      ),
    },
  ];

  return (
    <ModalLayout onClose={onClose} width="max-w-xl">
      <div className="flex flex-col gap-7 md:max-h-96 overflow-y-auto custom-scrollbar">
        <span className="font-semibold text-xl text-neutral-400">
          Add Teachers
        </span>

        <DataTable columns={columns} data={staffList} />

        <div className="flex gap-4 justify-end items-end">
          <CustomButton
            text="Cancel"
            color="bg-neutral-200"
            onClick={onClose}
          />
          <CustomButton
            text="Add"
            color="bg-primary-500"
            onClick={handleAssignTeacher}
          />
        </div>
      </div>
    </ModalLayout>
  );
}
